/**
 * Client for the AgentsServer prompt-assist endpoints (POST /prompt/analyze and
 * /prompt/resolve-dates). Powers PromptV2's live behaviors: auto-inserting `@` when a location is
 * expected, showing date hints, and rendering resolved-date chips. Mirrors HttpArtifactService
 * (baseUrl + bearer). The heavy Qwen/NER models run server-side; this is a thin fetch client.
 */

/** A personalized dropdown suggestion learned from the customer's history. */
export interface AnalyzeSuggestion {
    kind: string;  // "location" (value = place name, inserted as an @ mention) | "date" (value = the phrase)
    label: string;
    value: string;
}

/**
 * A unified suggest item from the host-provided search source (matches the TripApi `/air/prompt/suggest`
 * response item shape). The HTTP call itself is host-provided, so only the TYPE lives here.
 */
export interface SuggestItem {
    kind: 'location' | 'date';
    iata?: string;
    label: string;
    insert: string;   // location: "(IATA) Name"; date: the phrase
    score: number;
    region?: string;
    country?: string;
    /** Location can't be picked — already in the prompt (same place or same city). Shown greyed, not selectable. */
    disabled?: boolean;
}

/** Quick-path result — drives auto-`@`, date-hint visibility, and the combined predictions dropdown. */
export interface PromptAnalysis {
    locationExpected: boolean;
    locationPartialEmpty: boolean;
    dateExpected: boolean;
    locationCount: number;
    category: string; // "location" | "date" | "both" | "other"
    suggestions?: AnalyzeSuggestion[];
    /** The free-form location text being typed (search query for the combined dropdown), "@"-mentions stripped.
     * Server-owned (LocationCueDetector) — the client uses it verbatim instead of re-guessing from whitespace.
     * Optional for back-compat with an older server (falls back to the trailing-word heuristic). */
    locationPartial?: string;
    /** Issue #112 — the user is referring to a PERSON/passenger (embedding classifier). `personExpected` is the
     * server's thresholded flag; `personConfidence` ∈ [0,1] lets the `@` picker order/section its sources. Both
     * optional for back-compat with a server that predates the classifier (absent → treated as 0/false). */
    personExpected?: boolean;
    personConfidence?: number;
}

/** Slow-path resolved date — `start`/`length` anchor the chip; `display` is e.g. "quarta 8 de Jul". */
export interface ResolvedDate {
    start: number;
    length: number;
    text: string;
    resolvedIso: string;
    display: string;
}

/** A location the NER mapped in the slow pass — lets the client format locations pasted/dictated without a `@`. */
export interface ResolvedLocation {
    start: number;
    length: number;
    text: string;
}

/** Slow-path result: resolved dates + mapped locations. */
export interface PromptResolution {
    dates: ResolvedDate[];
    locations: ResolvedLocation[];
}

export class HttpPromptAssistService {
    private baseUrl: string;
    private accessToken: string;

    constructor(serverBaseUrl: string, accessToken: string) {
        this.baseUrl = serverBaseUrl.replace(/\/$/, '');
        this.accessToken = accessToken;
    }

    private get headers(): HeadersInit {
        return { 'Content-Type': 'application/json', Authorization: `Bearer ${this.accessToken}` };
    }

    /** Quick path (debounce ~300ms). `signal` lets the caller cancel a superseded keystroke. */
    async analyze(text: string, caret: number, signal?: AbortSignal): Promise<PromptAnalysis> {
        const res = await fetch(`${this.baseUrl}/prompt/analyze`, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({ text, caret, now: new Date().toISOString() }),
            signal,
        });
        if (!res.ok) throw new Error(`/prompt/analyze ${res.status}`);
        return (await res.json()) as PromptAnalysis;
    }

    /**
     * Slow path (trailing debounce). `sessionId` scopes the current prompt-composition session so the server
     * only persists a template when a new entity appears for it. Returns the resolved dates (empty on none/error).
     */
    async resolveDates(text: string, sessionId?: string, signal?: AbortSignal): Promise<PromptResolution> {
        const res = await fetch(`${this.baseUrl}/prompt/resolve-dates`, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({ text, now: new Date().toISOString(), sessionId }),
            signal,
        });
        if (!res.ok) throw new Error(`/prompt/resolve-dates ${res.status}`);
        const data = (await res.json()) as { dates?: ResolvedDate[]; locations?: ResolvedLocation[] };
        return { dates: data.dates ?? [], locations: data.locations ?? [] };
    }

    /**
     * Fire-and-forget on SEND: tells the server to LEARN from the final prompt (commit=true). Learning only on
     * commit means in-progress `@`-query partials typed while composing never get recorded as places.
     */
    async commitIntent(text: string, sessionId?: string): Promise<void> {
        try {
            await fetch(`${this.baseUrl}/prompt/resolve-dates`, {
                method: 'POST',
                headers: this.headers,
                body: JSON.stringify({ text, now: new Date().toISOString(), sessionId, commit: true }),
            });
        } catch { /* best-effort — learning must never block or break send */ }
    }
}
